import type { FinancialProduct } from '@interfaces/financialProduct';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { CheckboxField } from '@ovComponents/2-component/checkboxField/checkboxField';
import { TableCell, TableRow } from '@ovComponents/2-component/table/table';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../hooks/useLocalization';

export type ShelfSecurityItemProp = {
  security: FinancialProduct;
  picked: boolean;
  /** Already in the model: shown ticked and locked. */
  inModel: boolean;
  onToggle: (security: FinancialProduct) => void;
};

export const ShelfSecurityItem = ({
  security,
  picked,
  inModel,
  onToggle,
}: ShelfSecurityItemProp) => {
  const { t } = useTranslation(['modelPortfolios']);
  const { translateBackendString } = useLocalization();

  return (
    <TableRow
      hover={!inModel}
      onClick={() => onToggle(security)}
      data-testid={`add-securities-row-${security.id}`}
      sx={{ cursor: inModel ? 'default' : 'pointer' }}
    >
      <TableCell>
        <CheckboxField
          checked={inModel || picked}
          disabled={inModel}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          onChange={() => onToggle(security)}
          data-testid={`add-securities-checkbox-${security.id}`}
        />
      </TableCell>
      <TableCell>{security.ticker}</TableCell>
      <TableCell>
        {translateBackendString(security.translatedName)}
        {inModel && (
          <Box component='span' sx={{ ml: 1 }}>
            <Badge
              label={t('modelPortfolios:addSecuritiesDialog.inModel')}
              color='neutral'
            />
          </Box>
        )}
      </TableCell>
      <TableCell>
        {security.exchange && <Badge label={security.exchange} />}
      </TableCell>
      <TableCell>
        {translateBackendString(security.primaryAssetClass?.translatedName)}
      </TableCell>
      <TableCell>
        {translateBackendString(security.secondaryAssetClass?.translatedName)}
      </TableCell>
      <TableCell>
        {security.currency && <Badge label={security.currency} />}
      </TableCell>
      <TableCell number>
        {formatMoneyValue(security.currentPriceCents)}
      </TableCell>
    </TableRow>
  );
};
